// La matriz de permisos, en pantalla · DIRECTRIZ-EXPERIENCIA E4
//
// Una fila por módulo. Lo del puesto va marcado y no se toca desde aquí
// (se cambia el puesto); lo concedido aparte se marca distinto y se puede
// quitar; lo que no tiene se puede dar, con su motivo, por el mismo camino
// de siempre (`onConceder`). Búsqueda arriba y el resumen en una línea. El
// Supremo lo tiene todo y se le dice.
//
// ANEXO-CLIENTE-01 H-4 (20 sep 2026). El cliente vio sesenta casillas, la
// mitad vacías, en dos columnas enteras. Ahora:
//   · arriba, PLANTILLAS de oficio (Almacenista, Supervisor, Contadora,
//     Solo lectura) que dejan la pantalla en un clic;
//   · cada módulo va PLEGADO con su resumen de una línea y se abre al tocarlo
//     (al buscar se abren solos los que coinciden);
//   · dentro, la columna vacía no se dibuja: solo las acciones con algo.
import { useMemo, useState } from 'react';
import { ChevronDown, Lock, Search } from 'lucide-react';
import {
  NOMBRE_ACCION, matriz, resumen, buscar, resumenDeModulo, accionesConAlgo, resumenDePlantilla,
} from './permisos';

export default function MatrizDePermisos({
  catalogo = [], porRol = [], concedidos = [], esSupremo = false, puedeEditar = false,
  onConceder, onQuitar,
  // Plantillas de oficio (H-4). Sin ellas la pantalla funciona igual que antes.
  plantillas = [], onPlantilla,
}) {
  const [texto, setTexto] = useState('');
  const [dando, setDando] = useState(null);      // código que se está concediendo
  const [motivo, setMotivo] = useState('');
  const [abiertos, setAbiertos] = useState(() => new Set());
  const [aplicando, setAplicando] = useState(null);
  const [resultado, setResultado] = useState(null);
  const filas = useMemo(() => matriz(catalogo, porRol, concedidos), [catalogo, porRol, concedidos]);
  const visibles = useMemo(() => buscar(filas, texto), [filas, texto]);
  const buscando = texto.trim().length > 0;

  const conceder = async () => {
    if (!dando || motivo.trim().length < 3) return;
    await onConceder(dando, motivo.trim());
    setDando(null); setMotivo('');
  };

  const alternar = (modulo) => setAbiertos((s) => {
    const n = new Set(s);
    if (n.has(modulo)) n.delete(modulo); else n.add(modulo);
    return n;
  });

  const aplicar = async (p) => {
    setAplicando(p.clave); setResultado(null);
    try {
      const r = await onPlantilla(p.clave);
      setResultado({ ok: true, texto: r?.mensaje || `Plantilla «${p.nombre}» aplicada.` });
    } catch (e) {
      setResultado({ ok: false, texto: e?.message || 'No se pudo aplicar la plantilla. Vuelve a intentarlo.' });
    } finally {
      setAplicando(null);
    }
  };

  return (
    <div className="space-y-2">
      <p className="text-sm text-gray-800"><span className="font-semibold">En una línea:</span> {esSupremo ? 'Tiene todos los permisos de la empresa: es el Admin Supremo y su mapa no se edita.' : resumen(filas) + '.'}</p>

      {puedeEditar && !esSupremo && plantillas.length > 0 && onPlantilla && (
        <section aria-label="Plantillas de oficio" className="rounded-lg border border-gray-200 bg-gray-50 p-2">
          <p className="text-xs text-gray-700 mb-1.5">
            <span className="font-semibold">En un clic:</span> elige el oficio y se le dan los permisos que le faltan.
            Lo que ya tiene no se toca, y abajo puedes afinar uno por uno.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {plantillas.map((p) => {
              const r = resumenDePlantilla(p, filas);
              const nada = r.nuevos === 0;
              return (
                <button key={p.clave} type="button" onClick={() => aplicar(p)}
                  disabled={aplicando !== null || nada}
                  title={p.para_quien}
                  // P12 (29 sep 2026): el NOMBRE del botón es fijo y la cuenta
                  // va como descripción. Con la cuenta dentro del nombre, el
                  // botón «cambiaba de nombre» cada vez que se concedía algo y
                  // el censo de fachadas ya no lo reconocía al volver.
                  aria-label={`${p.nombre}: ${p.para_quien}`}
                  aria-describedby={`plantilla-${p.clave}-cuenta`}
                  className="rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-left text-xs hover:bg-gray-100 disabled:opacity-50">
                  <span className="block font-semibold text-gray-900">{aplicando === p.clave ? 'Aplicando…' : p.nombre}</span>
                  <span className="block text-[11px] text-gray-600">{p.para_quien}</span>
                  <span id={`plantilla-${p.clave}-cuenta`} className="block text-[11px] text-gray-500">{nada ? 'Ya lo tiene todo' : `Le daría ${r.nuevos} permiso${r.nuevos === 1 ? '' : 's'}`}</span>
                </button>
              );
            })}
          </div>
          {resultado && (
            <p role="status" className={`mt-1.5 text-xs ${resultado.ok ? 'text-green-800' : 'text-red-700'}`}>{resultado.texto}</p>
          )}
        </section>
      )}

      <label className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-2">
        <Search className="h-4 w-4 text-gray-500" aria-hidden="true" />
        <span className="sr-only">Buscar un permiso</span>
        <input value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Buscar: facturar, conteo, liberar…"
          className="w-full py-2 text-sm outline-none" />
      </label>
      {visibles.length === 0 && <p className="text-sm text-gray-600">Ningún permiso coincide con «{texto}». Prueba con otra palabra.</p>}

      <div className="divide-y divide-gray-100 rounded-lg border border-gray-200">
        {visibles.map((f) => {
          const abierto = buscando || abiertos.has(f.modulo);
          const idLista = `permisos-${f.modulo.replace(/\W+/g, '-').toLowerCase()}`;
          return (
            <section key={f.modulo} data-modulo={f.modulo}>
              <h5 className="m-0">
                <button type="button" onClick={() => alternar(f.modulo)} aria-expanded={abierto} aria-controls={idLista}
                  aria-label={f.modulo} aria-describedby={`${idLista}-resumen`}
                  className="flex w-full items-center justify-between gap-2 px-2.5 py-2 text-left text-sm hover:bg-gray-50">
                  <span>
                    <span className="font-semibold text-gray-900">{f.modulo}</span>
                    <span id={`${idLista}-resumen`} className="ml-2 text-xs text-gray-600">{esSupremo ? 'todo' : resumenDeModulo(f)}</span>
                  </span>
                  <ChevronDown className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${abierto ? '' : '-rotate-90'}`} aria-hidden="true" />
                </button>
              </h5>
              {abierto && (
                <div id={idLista} className="grid gap-2 px-2.5 pb-2 sm:grid-cols-2">
                  {/* La columna vacía no se dibuja: solo las acciones con algo. */}
                  {accionesConAlgo(f).map((a) => (
                    <div key={a}>
                      <p className="text-[11px] font-semibold uppercase text-gray-600">{NOMBRE_ACCION[a]}</p>
                      <ul className="space-y-1">
                        {f.celdas[a].map((p) => {
                          const tiene = esSupremo || p.origen === 'puesto' || p.origen === 'aparte';
                          const fijo = esSupremo || p.origen === 'puesto';
                          return (
                            <li key={p.codigo} className="flex items-start gap-1.5 text-xs">
                              <input type="checkbox" checked={tiene} disabled={fijo || !puedeEditar || p.origen === 'caducado'}
                                aria-label={`${p.nombre}${fijo ? ' (viene del puesto)' : ''}`}
                                title={p.descripcion || ''}
                                onChange={() => (tiene ? onQuitar?.(p.codigo) : setDando(p.codigo))}
                                className="mt-0.5 h-4 w-4" />
                              <span className={tiene ? 'text-gray-900' : 'text-gray-500'}>
                                {p.nombre}
                                {fijo && !esSupremo && <Lock className="ml-1 inline h-3 w-3 text-gray-500" aria-hidden="true" />}
                                {p.origen === 'aparte' && <span className="ml-1 rounded bg-amber-50 px-1 text-[10px] text-amber-800">aparte</span>}
                                {p.origen === 'caducado' && <span className="ml-1 rounded bg-gray-100 px-1 text-[10px] text-gray-600">caducado</span>}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </section>
          );
        })}
      </div>
      <p className="text-[11px] text-gray-600"><Lock className="inline h-3 w-3" aria-hidden="true" /> viene del puesto (se cambia cambiando el puesto) · <span className="rounded bg-amber-50 px-1 text-amber-800">aparte</span> concedido a esta persona.</p>
      {dando && (
        <div className="rounded-lg bg-gray-50 p-2 text-xs">
          <label className="block">
            Por qué se lo das
            <input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Lleva la tienda grande y cotiza compras" className="mt-0.5 w-full rounded border p-1 text-xs" />
            <span className="text-[10px] text-gray-600">Se queda escrito. Dentro de seis meses, esto es lo único que explicará por qué lo tiene.</span>
          </label>
          <div className="mt-2 flex gap-2">
            <button type="button" onClick={conceder} disabled={motivo.trim().length < 3} className="rounded bg-gray-900 px-2 py-1 text-xs font-semibold text-white disabled:opacity-50">Conceder</button>
            <button type="button" onClick={() => { setDando(null); setMotivo(''); }} className="rounded border px-2 py-1 text-xs">Cancelar</button>
            {motivo.trim().length < 3 && <span className="self-center text-[10px] text-gray-600">El botón se activa con el motivo.</span>}
          </div>
        </div>
      )}
    </div>
  );
}
